import classNames from 'classnames'
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import styles from './MessageDisplay.module.scss'

export type MessageType = 'info' | 'success' | 'warning' | 'error'

interface MessageDisplayProps {
  message: string | null
  onClose: () => void
  type?: MessageType
  isErrorMessage?: boolean
}

const VISIBLE_MS = 3500
// Covers the exit animation; also closes when there's no animationend (reduced motion, jsdom).
const LEAVE_FALLBACK_MS = 300

// Congic Core icon masters (assets/icons): info-circle, check-circle-broken, alert-triangle, x-circle.
const ICON_PATHS: Record<MessageType, string> = {
  info: 'M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z',
  success:
    'M22 11.0857V12.0057C21.9988 14.1621 21.3005 16.2604 20.0093 17.9875C18.7182 19.7147 16.9033 20.9782 14.8354 21.5896C12.7674 22.201 10.5573 22.1276 8.53447 21.3803C6.51168 20.633 4.78465 19.2518 3.61096 17.4428C2.43727 15.6338 1.87979 13.4938 2.02168 11.342C2.16356 9.19029 2.99721 7.14205 4.39828 5.5028C5.79935 3.86354 7.69279 2.72111 9.79619 2.24587C11.8996 1.77063 14.1003 1.98806 16.07 2.86572M22 4L12 14.01L9 11.01',
  warning:
    'M11.9998 8.99999V13M11.9998 17H12.0098M10.6151 3.89171L2.39019 18.0983C1.93398 18.8863 1.70588 19.2803 1.73959 19.6037C1.769 19.8857 1.91677 20.142 2.14613 20.3088C2.40908 20.5 2.86435 20.5 3.77487 20.5H20.2246C21.1352 20.5 21.5904 20.5 21.8534 20.3088C22.0827 20.142 22.2305 19.8857 22.2599 19.6037C22.2936 19.2803 22.0655 18.8863 21.6093 18.0983L13.3844 3.89171C12.9299 3.10654 12.7026 2.71396 12.4061 2.58211C12.1474 2.4671 11.8521 2.4671 11.5935 2.58211C11.2969 2.71396 11.0696 3.10655 10.6151 3.89171Z',
  error:
    'M15 9L9 15M9 9L15 15M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z',
}

const MessageDisplay: React.FC<MessageDisplayProps> = ({ message, onClose, type, isErrorMessage }) => {
  const resolvedType: MessageType = type ?? (isErrorMessage ? 'error' : 'success')
  const [leaving, setLeaving] = useState(false)
  const closed = useRef(false)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const close = useCallback(() => {
    if (closed.current) return
    closed.current = true
    onCloseRef.current()
  }, [])

  useEffect(() => {
    if (!message) return
    closed.current = false
    setLeaving(false)
    const timer = setTimeout(() => setLeaving(true), VISIBLE_MS)
    return () => clearTimeout(timer)
  }, [message])

  useEffect(() => {
    if (!leaving) return
    const timer = setTimeout(close, LEAVE_FALLBACK_MS)
    return () => clearTimeout(timer)
  }, [leaving, close])

  if (!message) {
    return null
  }

  return (
    <output
      className={classNames(styles.toast, styles[resolvedType], leaving && styles.leaving)}
      data-test="notification-message"
      data-type={resolvedType}
      data-leaving={leaving}
      onClick={() => setLeaving(true)}
      onAnimationEnd={() => leaving && close()}
    >
      <span className={styles.frame}>
        <svg className={styles.icon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d={ICON_PATHS[resolvedType]} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span className={styles.text}>{message}</span>
    </output>
  )
}

export default MessageDisplay
